import { SDK_VERSION } from '../version';
import { AuthError, BadRequestError, NetworkError, RateLimitError } from './errors';
import type { ClientOptions } from './types';

/**
 * Internal HTTP client used by every module. Wraps fetch with:
 *
 *   - timeout via AbortController (per-call or from ClientOptions)
 *   - retry with exponential backoff on 5xx / 429 / network errors
 *   - typed error mapping (AuthError / RateLimitError / BadRequestError /
 *     NetworkError / ProxyScrapeError)
 *   - User-Agent + optional Authorization injection
 *
 * Module code never touches fetch directly — that keeps timeout / retry /
 * error behavior consistent everywhere and gives us one place to add
 * telemetry, logging, or alternate transports later.
 */

const DEFAULT_BASE_URL = 'https://api.proxyscrape.com/v4';
const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_RETRIES = 3;

export interface RequestOptions {
  /** Override the request-level timeout. Falls back to ClientOptions.timeoutMs. */
  timeoutMs?: number;
  /** AbortSignal that lets the caller cancel mid-flight. */
  signal?: AbortSignal;
  /** Extra headers merged into the default set. */
  headers?: Record<string, string>;
  /**
   * Skip injection of the `api-token` header.
   *
   * Used when the request targets a third-party endpoint (e.g. ipinfo.io
   * for `getPublicIp`) where we must NOT leak the caller's API key.
   * Defaults to false; module code should leave it false for any call
   * hitting api.proxyscrape.com.
   */
  skipAuth?: boolean;
}

export interface RetryableRequest {
  url: string | URL;
  init?: RequestInit;
  /** If false, never retry — used for endpoints where idempotency is unclear. */
  retry?: boolean;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly apiKey: string | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly maxRetries: number;
  private readonly userAgent: string;

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.apiKey = opts.apiKey;
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.userAgent = opts.userAgent
      ? `proxyscrape-sdk/${SDK_VERSION} ${opts.userAgent}`
      : `proxyscrape-sdk/${SDK_VERSION}`;
  }

  /** Resolve a path against the base URL. Pass through absolute URLs as-is. */
  url(path: string, query?: Record<string, string | number | undefined | null>): URL {
    const base = /^https?:/.test(path)
      ? path
      : `${this.baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    const u = new URL(base);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null) continue;
        u.searchParams.set(k, String(v));
      }
    }
    return u;
  }

  /** Issue a request, parse JSON, and return the decoded body. */
  async getJson<T>(
    path: string,
    query?: Record<string, string | number | undefined | null>,
    opts: RequestOptions = {},
  ): Promise<T> {
    const res = await this.request(
      {
        url: this.url(path, query),
        init: { method: 'GET' },
      },
      opts,
    );
    return (await res.json()) as T;
  }

  /** Issue a request and return the raw text body — used for the text/csv export endpoints. */
  async getText(
    path: string,
    query?: Record<string, string | number | undefined | null>,
    opts: RequestOptions = {},
  ): Promise<string> {
    const res = await this.request(
      {
        url: this.url(path, query),
        init: { method: 'GET' },
      },
      opts,
    );
    return await res.text();
  }

  /**
   * Low-level request: handles timeout, retries, headers, error mapping.
   * Most module code should call getJson / getText instead.
   */
  async request(req: RetryableRequest, opts: RequestOptions = {}): Promise<Response> {
    const attempts = (req.retry ?? true) ? this.maxRetries + 1 : 1;
    let lastErr: unknown;

    for (let attempt = 0; attempt < attempts; attempt++) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs);

      // Chain the caller's AbortSignal so external cancellation works too.
      if (opts.signal) {
        if (opts.signal.aborted) ctrl.abort(opts.signal.reason);
        else
          opts.signal.addEventListener('abort', () => ctrl.abort(opts.signal?.reason), {
            once: true,
          });
      }

      const headers: Record<string, string> = {
        Accept: 'application/json',
        'User-Agent': this.userAgent,
        // Skip auth header for explicit opt-outs (third-party endpoints).
        // Without this guard, a `getPublicIp` call against ipinfo.io would
        // forward the user's ProxyScrape API key to a vendor we don't own.
        ...(this.apiKey && !opts.skipAuth ? { 'api-token': this.apiKey } : {}),
        ...(req.init?.headers as Record<string, string> | undefined),
        ...opts.headers,
      };

      try {
        const res = await this.fetchImpl(req.url, {
          ...req.init,
          headers,
          signal: ctrl.signal,
        });
        clearTimeout(timer);

        if (res.ok) return res;

        // Map status codes to typed errors.
        const status = res.status;
        const bodyText = await safeText(res);

        if (status === 401 || status === 403) {
          throw new AuthError(`Authentication failed (${status}): ${bodyText}`, status);
        }

        // ProxyScrape uses non-standard 432/433/434 for rate-limit windows;
        // 429 is the standard one. Treat all four the same.
        if (status === 429 || status === 432 || status === 433 || status === 434) {
          const retryAfter = parseRetryAfter(res.headers.get('Retry-After'));
          if (attempt < attempts - 1) {
            lastErr = new RateLimitError(
              `Rate limited (${status}): ${bodyText}`,
              status,
              retryAfter,
            );
            await sleep(backoffMs(attempt, retryAfter));
            continue;
          }
          throw new RateLimitError(`Rate limited (${status}): ${bodyText}`, status, retryAfter);
        }

        if (status >= 500 && status < 600) {
          if (attempt < attempts - 1) {
            lastErr = new ProxyScrapeError(`Server error (${status}): ${bodyText}`);
            await sleep(backoffMs(attempt, null));
            continue;
          }
          throw new ProxyScrapeError(`Server error (${status}): ${bodyText}`);
        }

        // Any other 4xx — non-recoverable.
        throw new BadRequestError(`Bad request (${status}): ${bodyText}`, status);
      } catch (err) {
        clearTimeout(timer);

        // Re-throw SDK errors that we've already mapped.
        if (err instanceof AuthError || err instanceof BadRequestError) throw err;
        if (err instanceof RateLimitError && attempt >= attempts - 1) throw err;

        // AbortError / TypeError / fetch-network failures.
        const isAbort =
          err instanceof Error && (err.name === 'AbortError' || err.message.includes('aborted'));
        const isCallerAbort = opts.signal?.aborted === true;
        if (isAbort && isCallerAbort) throw err; // Honor caller cancellation immediately.

        if (attempt < attempts - 1) {
          lastErr = err;
          await sleep(backoffMs(attempt, null));
          continue;
        }
        throw new NetworkError(err instanceof Error ? err.message : 'fetch failed', err);
      }
    }

    // Should be unreachable, but TS can't see that without exhaustive flow analysis.
    throw lastErr instanceof Error ? lastErr : new ProxyScrapeError('exhausted retries');
  }
}

// ProxyScrapeError is re-imported here only for the throw above where we
// downgrade a typed error to the base class. Importing it at the top avoids
// circular module concerns.
import { ProxyScrapeError } from './errors';

function backoffMs(attempt: number, retryAfterSeconds: number | null): number {
  if (retryAfterSeconds != null) return Math.max(0, retryAfterSeconds * 1000);
  // Exponential backoff with jitter: 250, 500, 1000, 2000, 4000 ms ± 30%.
  const base = 250 * 2 ** attempt;
  const jitter = (Math.random() * 0.6 - 0.3) * base;
  return Math.max(0, Math.round(base + jitter));
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const n = Number(header);
  if (Number.isFinite(n)) return n;
  // HTTP-date form. Convert delta from now.
  const t = Date.parse(header);
  if (Number.isFinite(t)) return Math.max(0, Math.round((t - Date.now()) / 1000));
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
